import { clsx, type ClassValue } from 'clsx';
import zipcodes from 'zipcodes';
import { twMerge } from 'tailwind-merge';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Dependent, Products, Spouse } from '@/state/types';
import { sendGTMEvent } from '@next/third-parties/google';
import { Tab } from '@/app/(portal)/complete-enrollment/types';
import { ParsedPlan, PlanRate } from '@/types';
import { Plan } from '@/state/types';
import { PlanComparisonResponse } from '@/app/(portal)/quote/fetch-plans/route';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getExpirationDate(expirationDate: string) {
  const match = expirationDate.match(/(\d{2})\/(\d{2})/);
  if (!match) return expirationDate;

  const [_, expirationMonth, expirationYear] = match;
  return {
    expirationMonth,
    expirationYear: `20${expirationYear}`,
  };
}

export const generateEffectiveDates = () => {
  const currentDate = new Date();
  const options = [
    getFirstOfNextThreeMonths(currentDate, 1),
    getFirstOfNextThreeMonths(currentDate, 2),
    getFirstOfNextThreeMonths(currentDate, 3),
  ];
  return options;
};

export const getFirstOfNextThreeMonths = (
  date: Date,
  monthsToAdd: number
): string => {
  const newDate = new Date(
    date.getFullYear(),
    date.getMonth() + monthsToAdd,
    1
  );
  return newDate
    .toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    .toString(); // Format as January 1, 2025
};

export function getEffectiveDate(paymentMethod: string, selectedDate?: string) {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const firstOfNextMonth = dayjs()
    .add(1, 'month')
    .date(1)
    .hour(6)
    .tz('America/New_York')
    .format('YYYY-MM-DD');

  if (dayjs(selectedDate).isAfter(firstOfNextMonth)) {
    return dayjs(selectedDate).format('YYYY-MM-DD');
  }
  // If the payment method is "mailInCheck" and the current date is after the 15th,
  // set the effective date to the first of the month after next month
  // Otherwise, set it to the first of next month

  if (
    (paymentMethod === 'mailInCheck' || paymentMethod === Tab.MailInCheck) &&
    dayjs().tz('America/New_York').date() > 15
  ) {
    return dayjs()
      .add(2, 'month')
      .date(1)
      .hour(6)
      .tz('America/New_York')
      .format('YYYY-MM-DD');
  }
  return firstOfNextMonth;
}

export function convertToMonthDayYear(date: string) {
  const [year, month, day] = date.split('-');
  return `${month}/${day}/${year}`;
}

export function convertToMonthYear(date: string) {
  const [year, month] = date.split('-');
  return `${month}/${year.slice(-2)}`;
}

export function formatStartDate(startDate: string) {
  return dayjs(startDate).format('MMMM DD, YYYY');
}

export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function getNamesCoveredText(
  prefix: string,
  firstName: string,
  dependents?: Dependent[],
  spouse?: Spouse
) {
  const names = [firstName];

  if (spouse?.firstName) {
    names.push(spouse.firstName);
  }

  if (dependents?.length) {
    const dependentNames = dependents.map(({ firstName }) => firstName);
    names.push(
      ...dependentNames.filter((name): name is string => name !== undefined)
    );
  }

  const formattedNames =
    names.length > 2
      ? `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
      : names.join(' and ');

  return `${prefix} ${formattedNames}`;
}

/**
 * Checks if the given input is empty.
 *
 * This function considers the following as empty:
 * - `null` or `undefined`
 * - Arrays with a length of 0
 * - Objects with no own enumerable properties
 * - Functions with no own enumerable properties
 *
 * @param it - The input to check for emptiness. Defaults to an empty array.
 * @returns `true` if the input is empty, `false` otherwise.
 */
export function isEmpty(it: any = []): boolean {
  if (!it) {
    return true;
  }
  if (it && typeof it.length === 'number') {
    return it.length === 0;
  }
  if (typeof it === 'object' || typeof it === 'function') {
    return Object.keys(it).length === 0;
  }
  return true;
}

/**
 * Checks if the provided data object contains any errors.
 *
 * This function recursively traverses the object to determine if any of its values
 * are instances of the `Error` class.
 *
 * @param data - The object to check for errors. It should be a record with string keys and any type of values.
 * @returns `true` if any value in the object or its nested objects is an instance of `Error`, otherwise `false`.
 */
export const hasErrors = (data: Record<string, any>): boolean => {
  const checkErrors = (obj: Record<string, any>): boolean => {
    return Object.values(obj).some((item) => {
      if (item instanceof Error) {
        return true;
      }
      if (item && typeof item === 'object') {
        return checkErrors(item);
      }
      return false;
    });
  };
  return checkErrors(data);
};

/**
 * Formats a given effective date string to the 'YYYY-MM-DD' format
 * in the 'America/New_York' timezone.
 *
 * @param effectiveDate - The date string to be formatted.
 * @returns The formatted date string in 'YYYY-MM-DD' format.
 */
export function formatEffectiveDate(effectiveDate: string): string {
  dayjs.extend(utc);
  return dayjs.utc(effectiveDate).format('YYYY-MM-DD');
}

/**
 *
 * @param existingDependents
 * @param newDependents
 * @returns The merged dependents object between jotai state during a cross sell and form state
 */
export function mergeDependents(
  existingDependents: Dependent[] = [],
  newDependents: Dependent[] = []
): Dependent[] {
  const updatedDependents = newDependents.map((newDependent) => {
    const matchingExisting = existingDependents.find(
      (existing) => existing.id === newDependent.id
    );
    return matchingExisting
      ? { ...matchingExisting, ...newDependent }
      : newDependent;
  });
  return updatedDependents;
}

/**
 * Merges the existing spouse information with the new spouse information.
 * If the new spouse information is undefined, it returns the existing spouse information.
 * If both are undefined, it returns undefined.
 *
 * @param existingSpouseInfo - The existing spouse information.
 * @param newSpouseInfo - The new spouse information.
 * @returns The merged spouse information or undefined if both are undefined.
 */
export function mergeSpouse(
  existingSpouseInfo: Spouse | undefined,
  newSpouseInfo: Spouse | undefined
): Spouse | undefined {
  if (!newSpouseInfo) {
    return undefined;
  }
  return {
    ...existingSpouseInfo,
    ...newSpouseInfo,
  };
}

export function resetDataLayer() {
  sendGTMEvent({
    event: 'custom_event',
    event_name: 'data_layer_reset',
    product: null,
    plan: null,
    age_range: null,
    payment_method: null,
  });
}

export const calculateAge = (dob: string): number => {
  const birthDate = dayjs(dob);
  const today = dayjs();
  return today.diff(birthDate, 'year', true) | 0; // Use precise diff and truncate to integer
};

/**
 * Finds the closest age value from the provided age options based on the current age.
 * It iterates through the age options in reverse order and returns the first value
 * that is less than or equal to the current age.
 *
 * @param currentAge - The current age to compare against.
 * @param ageOptions - An array of objects containing age values.
 * @returns The closest age value as a string.
 */
export function getClosestAgeValue(
  currentAge: number,
  ageOptions: { value: string }[]
): string {
  for (let i = ageOptions.length - 1; i >= 0; i--) {
    const ageValue = parseInt(ageOptions[i].value, 10);
    if (currentAge >= ageValue) {
      return ageOptions[i].value;
    }
  }
  return ageOptions[0].value;
}
export function getEISProductCode(product: string) {
  switch (product) {
    case 'Dental':
      return 'PREC-IDPPO';
    case 'Vision':
      return 'IVSP';
    case 'Accident':
      return 'PREC-IA';
    default:
      return '';
  }
}

/**
 * Removes all extra whitespace characters from a string.
 * @param s The input string.
 * @returns The string with all whitespace removed.
 */
export function trimWhitespaces(s: string): string {
  return s.trim().replace(/\s+/g, ' ');
}

/**
 * Fetches and parses plans based on the provided plan rates, state, and age.
 * It sends a Google Tag Manager event and filters the plans based on the parsed plans.
 *
 * @param planRates - The array of plan rates to be filtered.
 * @param state - The state code used for fetching plans.
 * @param age - (Optional) The age of the primary insured individual.
 * @returns An array of filtered plans with additional information.
 */
export async function getParsedPlans(
  planRates: PlanRate[] = [],
  state: string,
  age?: string
) {
  if (!planRates || planRates.length === 0) {
    return [];
  }

  const product = planRates?.[0]?.productType?.toLowerCase()!;
  const { parsedPlans } = await fetchedPlansContent(state, product);

  // Build a lookup object using planTerm as the key
  const plansByTerm = Array.isArray(parsedPlans)
    ? parsedPlans.reduce(
        (acc, plan) => {
          if (plan.planTerm) acc[plan.planTerm] = plan;
          return acc;
        },
        {} as Record<string, any>
      )
    : parsedPlans;

  sendGTMEvent({
    event: 'custom_event',
    event_name: 'quote_created',
    product: product.toLowerCase(),
    age_range: age,
  });

  const filteredPlans = planRates
    ?.filter((plan) => (plan.planCode ? !!plansByTerm[plan.planCode] : false))
    .map((plan) =>
      plan.planCode
        ? {
            ...plan,
            planName: plansByTerm[plan.planCode].planName,
            description: plansByTerm[plan.planCode].description,
            planDetailsPdf: plansByTerm[plan.planCode].planDetailsPdf,
            limitationsAndExclusions:
              plansByTerm[plan.planCode].limitationsAndExclusions,
          }
        : null
    )
    .filter(Boolean);

  return filteredPlans as ParsedPlan[];
}

/**
 * Fetches and parses plan content for a given state and product.
 *
 * @param state - The state code for which to fetch plans.
 * @param product - The product code for which to fetch plans.
 * @returns A promise that resolves to an object containing parsed plans, where each plan term maps to its plan details.
 */
async function fetchedPlansContent(
  state: string,
  product: string
): Promise<PlanComparisonResponse> {
  return fetch(`/quote/fetch-plans?state=${state}&product=${product}`).then(
    (response) => response.json()
  );
}

/**
 * Calculates the age based on the provided date of birth (dob) and a reference date.
 * It determines the age by finding the difference in years between the reference date and the birth date.
 *
 * @param dob - The date of birth in the format 'YYYY-MM-DD'.
 * @param refDate - The reference date in the format 'YYYY-MM-DD' to calculate the age against.
 * @returns The calculated age as a number.
 */
export function calculateAgeByDate(dob: string, refDate: string): number {
  const birthDate = dayjs(dob);
  const referenceDate = dayjs(refDate);
  return referenceDate.diff(birthDate, 'year', true) | 0;
}

/**
 * Feature flag configuration for accident purchase restriction.
 */
export type AccidentPurchaseRestrictionConfig = {
  states: string[];
  toggle: boolean;
};

/**
 * Validates spouse age for Accident product in configured restricted states.
 * @param product - The insurance product type
 * @param spouseAge - The spouse's age (string or number)
 * @param zipCode - The zip code used to resolve state
 * @param accidentPurchaseRestrictionOver65 - Dynamic state/toggle feature flag config
 * @returns Object with isValid boolean and error message if invalid
 */
export function validateAccidentSpouseAge(
  product: string,
  spouseAge: string | number | undefined,
  zipCode: string,
  accidentPurchaseRestrictionOver65?: AccidentPurchaseRestrictionConfig
): { isValid: boolean; errorMessage?: string } {
  const parsedSpouseAge = typeof spouseAge === 'string' ? parseInt(spouseAge, 10) : spouseAge;
  const stateCode = zipcodes.lookup(zipCode)?.state;
  const restrictedStates = Array.isArray(accidentPurchaseRestrictionOver65?.states) ? accidentPurchaseRestrictionOver65.states : [];

  if (
    product === Products.Accident &&
    parsedSpouseAge !== undefined &&
    !Number.isNaN(parsedSpouseAge) &&
    parsedSpouseAge >= 65 &&
    accidentPurchaseRestrictionOver65?.toggle === true &&
    restrictedStates.includes(stateCode ?? '')
  ) {
    return {
      isValid: false,
      errorMessage: 'Spouse/Partner age must be under 65 to qualify',
    };
  }
  return { isValid: true };
}
